/**
 * CUE sheet parsing and CD sector geometry.
 *
 * A .cue is a plain-text table of contents for one or more .bin files. We need
 * exactly two things out of it: which FILE holds the data track, and where in
 * that file the track starts. Everything else (audio tracks, ISRC, CATALOG,
 * FLAGS, CDTEXT) is noise for our purposes and is skipped rather than rejected —
 * real-world cues from twenty different rippers are full of it.
 *
 * ## Sector geometry
 *
 * A CD sector is 2352 bytes on the physical disc, but only some of that is user
 * data. A `.bin` dumped as MODE1/2352 keeps the whole raw sector, so the 2048
 * bytes we want are buried:
 *
 *   [ 12 sync ][ 4 header ][ 2048 user data ][ 288 EDC/ECC ]
 *
 * A "cooked" MODE1/2048 image has already had that stripped, so it is a plain
 * ISO and the user bytes are contiguous. That distinction is the whole reason
 * this module exists: for cooked images a file extent is one `Blob.slice`, and
 * for raw images every 2048 bytes needs lifting out of a 2352-byte frame.
 *
 * Mode 2 Form 1 (`MODE2/2352`) puts an 8-byte subheader after the header, so
 * the user data starts at 24. `MODE2/2336` is the same sector with the sync and
 * header already stripped. Both appear in the wild for mixed-mode discs; the
 * Fallout 2 disc is MODE1/2352, but supporting them costs two table rows.
 */

export interface SectorGeometry {
  /** Bytes per sector as stored in the file. */
  rawSize: number;
  /** Offset of the 2048-byte user area within the stored sector. */
  headerOffset: number;
  /** Always 2048 for the data modes we can read. */
  userSize: number;
}

export const USER_SIZE = 2048;

/** `true` when user bytes are contiguous, so an extent is a single slice. */
export function isCooked(g: SectorGeometry): boolean {
  return g.rawSize === g.userSize && g.headerOffset === 0;
}

const GEOMETRY: Record<string, SectorGeometry | null> = {
  'MODE1/2048': { rawSize: 2048, headerOffset: 0, userSize: USER_SIZE },
  'MODE1/2352': { rawSize: 2352, headerOffset: 16, userSize: USER_SIZE },
  'MODE2/2048': { rawSize: 2048, headerOffset: 0, userSize: USER_SIZE },
  'MODE2/2336': { rawSize: 2336, headerOffset: 8, userSize: USER_SIZE },
  'MODE2/2352': { rawSize: 2352, headerOffset: 24, userSize: USER_SIZE },
  // Present so the parser can name them in an error rather than shrug.
  AUDIO: null,
  'CDG/2448': null,
  'CDI/2336': null,
  'CDI/2352': null,
};

/** All geometries worth probing when we have to guess, best-first. */
export const CANDIDATE_GEOMETRIES: SectorGeometry[] = [
  { rawSize: 2352, headerOffset: 16, userSize: USER_SIZE }, // MODE1/2352 — the usual raw rip
  { rawSize: 2048, headerOffset: 0, userSize: USER_SIZE }, // MODE1/2048 — a plain .iso
  { rawSize: 2352, headerOffset: 24, userSize: USER_SIZE }, // MODE2/2352 form 1
  { rawSize: 2336, headerOffset: 8, userSize: USER_SIZE }, // MODE2/2336
];

export function geometryForMode(mode: string): SectorGeometry | null {
  return GEOMETRY[mode.toUpperCase()] ?? null;
}

export function isKnownMode(mode: string): boolean {
  return mode.toUpperCase() in GEOMETRY;
}

export interface CueTrack {
  number: number;
  /** As written in the cue, upper-cased, e.g. `MODE1/2352`. */
  mode: string;
  /** Sector geometry, or null for AUDIO and other unreadable track types. */
  geometry: SectorGeometry | null;
  /** FILE this track lives in, exactly as spelled in the cue. */
  fileName: string;
  /**
   * Sector index of INDEX 01 *within its FILE*.
   *
   * Cue INDEX times are relative to the start of the current FILE — which for a
   * single-file cue is also the start of the disc, and for a file-per-track cue
   * is almost always 00:00:00. PREGAP is deliberately not added: a pregap
   * declared with the PREGAP command is silence the drive generates, and is not
   * present in the file's bytes.
   */
  startSector: number;
}

export interface CueSheet {
  tracks: CueTrack[];
  /** Distinct FILE names in cue order. */
  files: string[];
}

export class CueParseError extends Error {}

/** `MM:SS:FF` at 75 frames (sectors) per second. */
export function msfToSector(msf: string): number {
  const m = /^(\d+):(\d+):(\d+)$/.exec(msf.trim());
  if (!m) throw new CueParseError(`Bad INDEX time "${msf}" — expected MM:SS:FF.`);
  return (Number(m[1]) * 60 + Number(m[2])) * 75 + Number(m[3]);
}

/**
 * Splits a cue line into tokens, honouring double quotes.
 *
 * Necessary because filenames on a CD-ROM rip routinely contain spaces
 * (`FILE "Fallout 2 (USA).bin" BINARY`), and a naive whitespace split turns one
 * filename into three tokens.
 */
function tokenize(line: string): string[] {
  const out: string[] = [];
  const re = /"([^"]*)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) out.push(m[1] !== undefined ? m[1] : m[2]);
  return out;
}

export function parseCue(text: string): CueSheet {
  // Cue sheets are frequently CRLF and occasionally carry a UTF-8 BOM.
  const lines = text.replace(/^﻿/, '').split(/\r\n|\r|\n/);
  const tracks: CueTrack[] = [];
  const files: string[] = [];
  let currentFile: string | null = null;
  let pending: CueTrack | null = null;

  const commit = () => {
    if (pending) tracks.push(pending);
    pending = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('REM ') || line === 'REM') continue;
    const tok = tokenize(line);
    const cmd = tok[0]?.toUpperCase();

    if (cmd === 'FILE') {
      commit();
      currentFile = tok[1] ?? '';
      if (!currentFile) throw new CueParseError('FILE line has no filename.');
      if (!files.includes(currentFile)) files.push(currentFile);
    } else if (cmd === 'TRACK') {
      commit();
      if (!currentFile) throw new CueParseError('TRACK appears before any FILE.');
      const number = Number(tok[1]);
      const mode = (tok[2] ?? '').toUpperCase();
      if (!Number.isFinite(number)) throw new CueParseError(`Bad TRACK number "${tok[1]}".`);
      pending = {
        number,
        mode,
        geometry: geometryForMode(mode),
        fileName: currentFile,
        // A track with no INDEX 01 at all is malformed, but assuming 0 is
        // friendlier than refusing the disc and is right for the common case.
        startSector: 0,
      };
    } else if (cmd === 'INDEX' && pending) {
      // INDEX 00 is the pregap *inside* the file; INDEX 01 is the track proper.
      if (Number(tok[1]) === 1) pending.startSector = msfToSector(tok[2] ?? '00:00:00');
    }
    // PREGAP / POSTGAP / FLAGS / ISRC / CATALOG / PERFORMER / TITLE: ignored.
  }
  commit();

  if (!tracks.length) throw new CueParseError('No TRACK entries found — is this really a .cue sheet?');
  return { tracks, files };
}

/** The first track we can actually read a filesystem out of. AUDIO is skipped. */
export function firstDataTrack(sheet: CueSheet): CueTrack | null {
  return sheet.tracks.find((t) => t.geometry !== null) ?? null;
}
